import { getEnvUrl } from '../../constants';

// Helper function to retry fetching auth tokens with exponential backoff
const fetchAuthTokensWithRetry = async (
  maxAttempts = 20,
  delayMs = 100
): Promise<{ authToken: string | null; authVersion: string | null }> => {
  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      const authToken =
        typeof window !== 'undefined' && window.localStorage ? window.localStorage.getItem('x-access-token') : null;
      const authVersion =
        typeof window !== 'undefined' && window.localStorage ? window.localStorage.getItem('x-access-version') : null;

      // If we have both tokens, return them
      if (authToken && authVersion) {
        return { authToken, authVersion };
      }

      // If this is not the last attempt, wait before retrying
      if (attempt < maxAttempts) {
        await new Promise(resolve => globalThis.setTimeout(resolve, delayMs));
      }
    } catch (error) {
      console.warn(`Attempt ${attempt} to fetch auth tokens failed:`, error);
      if (attempt < maxAttempts) {
        await new Promise(resolve => globalThis.setTimeout(resolve, delayMs));
      }
    }
  }

  // If we've exhausted all attempts, return null values
  console.warn(`Failed to fetch auth tokens after ${maxAttempts} attempts`);
  return { authToken: null, authVersion: null };
};

export const webBuilderRequest = async (
  path: string,
  {
    method = 'GET',
    body,
    headers = {},
    queryParams,
    signal,
  }: { method?: string; body?: any; headers?: any; queryParams?: any; signal?: any }
): Promise<any> => {
  try {
    // Fetch auth tokens with retry mechanism
    const { authToken, authVersion } = await fetchAuthTokensWithRetry();

    // Construct the URL
    const url = new URL(`${getEnvUrl('webBuilderApiUrl')}${path.startsWith('/') ? path : `/${path}`}`);

    // Add query parameters if provided
    if (queryParams) {
      Object.keys(queryParams).forEach(key => url.searchParams.append(key, queryParams[key]));
    }

    // Prepare the headers
    const combinedHeaders = {
      'Content-Type': 'application/json',
      'x-access-token': authToken,
      'x-access-version': authVersion,
      ...headers,
    };

    // Make the request
    const response = await fetch(url, {
      method,
      headers: combinedHeaders,
      body: body ? (typeof body === 'string' ? body : JSON.stringify(body)) : undefined,
      signal,
    });

    // Check for errors
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error(`Error while fetching web-builder: `, error);
    throw error;
  }
};
